import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { produce } from 'immer';
import { chain, keyBy } from 'lodash';
import { SIcon } from '@chargebee/sting-react';
import { NavIcons } from '@bbkAdminComponents/app-layout/nav';
import { AsyncThemeWrapper } from '@bbkAdminComponents/async-theme-provider';
import { renderCategoryName } from '@bbkAdminComponents/experiences/offers/edit-offer-utils';
import {
  PreviewOverlay,
  PreviewWrapper,
} from '@bbkAdminComponents/experiences/previews';
import {
  PivotTableComponent,
  type PivotT,
} from '@bbkAdminComponents/pivot-table/pivot-table';
import { PivotTableTooltipContent } from '@bbkAdminComponents/pivot-table/pivot-table-content-tooltip';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/play-context';
import { type CancelPlayReportProps } from '@bbkAdminComponents/unified-plays/reporting/reports/cancel-page/cancel-page-reports';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  selectFromResultTyped,
  useAppDispatch,
} from '@bbkAdminRedux/redux_utils';
import {
  appPersistSlice,
  selectOfferPerfReportNav,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { billingConfigSlice } from '@bbkAdminRedux/rtkq/billing-config.slice';
import {
  DEFAULT_BRAND_ID,
  useGetBrandOptions,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { companyAppSettingsSlice } from '@bbkAdminRedux/rtkq/company-app-settings.slice';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import {
  useSelectAllFunnelsEntities,
  useSelectFunnelsLoading,
} from '@bbkAdminRedux/rtkq/funnels.slice';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import {
  offersAndLossAversionsSlice,
  useSelectAllOffersAndLossAversions,
} from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import {
  reportsSlice,
  ReportType,
  type GetReportV2Args,
  type OfferPerfCtaPoint,
  type OfferPerformanceCtaReport,
  type OfferPerformanceV2Report,
  type OfferPerfV2Point,
  type ReportFilter,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import {
  rulesSlice,
  RuleType,
  useSelectAllRulesEntities,
} from '@bbkAdminRedux/rtkq/rules.slice';
import {
  isLossAversion,
  isModalCard,
  type RestLossAversionCardOrModalCard,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { NumberKit } from '@bbkAdminUtils/number-kit';
import { usePlans } from '@bbkAdminUtils/plans';
import { useStrings } from '@bbkAdminUtils/strings';
import CardFactory from '@bbkFunnelComponents/card-factory';

const PlayOfferPerf: React.FC<CancelPlayReportProps> = ({ play }) => {
  const dispatch = useAppDispatch();
  const appKey = useSelector(selectAppId);
  const brandOptions = useGetBrandOptions({ appKey });
  const strings = useStrings();
  const brandId = play.brand_id ?? DEFAULT_BRAND_ID;

  const selectedBrand = useMemo(() => {
    return brandOptions.find((b) => b.value === brandId);
  }, [brandOptions, brandId]);

  const isDemoData = useShowPlayDemoData();

  const isNestedAudiencesEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_NESTED_AUDIENCES_IN_PERF_REPORTS
  );

  const { isStarterFreePlan } = usePlans();
  const { data: appSettings } =
    companyAppSettingsSlice.endpoints.getAppSettings.useQuery({ appKey });
  const mrrActive = appSettings?.has_revenue;
  const exportBtnTooltip = {
    content: strings.UPGRADE_PLAN({
      description: strings.UPGRADE_TO_STARTER_PREMIUM_EXPORT,
    }),
    tooltipOptions: {
      placement: 'left',
    },
  };

  const { groupBy: initGroupByOpts, visibleColumns: initVisibleColumns } =
    useSelector(selectOfferPerfReportNav(appKey));

  const billingIntegration = useSelectBillingIntegration({ appKey });

  const { data: billingConfig } =
    billingConfigSlice.endpoints.getBillingConfig.useQuery(
      billingIntegration ? { app_key: appKey } : skipToken
    );
  const isRepurchaseAttributionEnabled =
    billingConfig?.repurchase_based_attribution;

  const { startDate: start, endDate: end } = useSelectDatePicker();
  const reportType = ReportType.offer_performance_funnel_audiences_cta;
  const request = {
    report_type: reportType,
  };

  const commonArgs = {
    appKey,
    start,
    end,
    filters: [
      [
        {
          field: 'play_id',
          operator: 'equals' as const,
          value: play.id,
        } satisfies ReportFilter,
      ],
    ],
    context: isDemoData ? BBKRequestContext.offer_perf : undefined,
  };
  const offerPerfArgs: GetReportV2Args = {
    ...commonArgs,
    report_type_requests: [request],
  };
  const opts = {
    skip: !appKey || !start || !end,
    selectFromResult: selectFromResultTyped<
      OfferPerformanceV2Report | OfferPerformanceCtaReport
    >,
  };
  const {
    data: reportData,
    isFetching: isReportFetching,
    isError,
  } = reportsSlice.endpoints.getReportV2.useQuery(offerPerfArgs, opts);

  const dataOffers = useMemo(() => {
    if (!reportData || isError) return [];
    return (reportData as OfferPerformanceCtaReport).offers[0];
  }, [reportData, isError]);

  const ctaDataOffers = useMemo(() => {
    if (!reportData || isError) return [];
    const offerCtaData = (reportData as OfferPerformanceCtaReport).offers[1];
    const offerData = (reportData as OfferPerformanceCtaReport).offers[0];
    if (!Array.isArray(offerCtaData)) {
      return [];
    }
    // concat offerCtaData and offerData
    return [
      ...offerCtaData.map((x) => ({
        ...x,
        totalViews: 0,
        ctaTotalViews: x.totalViews,
      })),
      ...offerData.map(
        (x) =>
          ({
            funnelAudContentCta: `${x.funnelAudContent},l_0`,
            totalViews: x.totalViews,
          }) as OfferPerfCtaPoint
      ),
    ];
  }, [reportData, isError]);

  const offerCtaMap = chain(ctaDataOffers)
    .map((ctaPoint) => {
      const contentCta = ctaPoint.funnelAudContentCta.split(',').slice(-2);
      const offerId = contentCta[0] as string;
      const ctaId = contentCta[1] as string;
      return { offer_id: offerId, cta_id: ctaId };
    })
    .uniqBy((x) => [x.offer_id, x.cta_id].join('-'))
    .value();
  const { data: ctasText } =
    offersAndLossAversionsSlice.endpoints.getCtaTextByOfferIdCtaId.useQuery(
      { appKey, body: offerCtaMap },
      { skip: ctaDataOffers.length === 0 }
    );

  const ctasTextMap = useMemo(
    () => keyBy(ctasText?.result ?? [], (x) => x.cta_id),
    [ctasText]
  );

  const offersAndLossAversions = useSelectAllOffersAndLossAversions({
    appKey,
    context: BBKRequestContext.offer_perf,
  });
  const offersAndLAsByIdMap = useMemo(
    () => keyBy(offersAndLossAversions, (x) => x.id),
    [offersAndLossAversions]
  );
  const rulesArgs = {
    appKey,
    context: BBKRequestContext.offer_perf,
    includeAdditionalRules: [RuleType.SPLIT],
    brandId,
  };
  const { isFetching: isRulesFetching } =
    rulesSlice.endpoints.getRules.useQuery(rulesArgs);
  const rulesMap = useSelectAllRulesEntities(rulesArgs);

  const funnelsArgs = {
    appKey,
    context: BBKRequestContext.offer_perf,
    brandId,
  };
  const { isFetching: isFunnelsFetching } =
    useSelectFunnelsLoading(funnelsArgs);
  const funnelsMap = useSelectAllFunnelsEntities(funnelsArgs);

  const isFetching = isReportFetching || isRulesFetching || isFunnelsFetching;

  const getFunnelAudienceContent = (row: RowType): 'None' | string =>
    (row as OfferPerfCtaPoint).funnelAudContentCta ??
    (row as OfferPerfV2Point).funnelAudContent;

  type ReportTags = {
    funnelId: string;
    audience1Id: string;
    audience2Id?: string;
    audience3Id?: string;
    audience4Id?: string;
    offerId: string;
    ctaId?: string;
  };
  const parseFunnelAudienceContent = (row: RowType): ReportTags => {
    const isCtaPoint = Boolean((row as OfferPerfCtaPoint).funnelAudContentCta);
    const funnelAudContentCta = isCtaPoint
      ? (row as OfferPerfCtaPoint).funnelAudContentCta
      : (row as OfferPerfV2Point).funnelAudContent;

    // format 'funnelId,...audienceIds,offerId'
    // or 'funnelId,...audienceIds,offerId,ctaId'
    // may be 0 or more audienceId
    const tags = funnelAudContentCta.split(',');

    const funnelIdIdx = 0;
    const funnelId = tags.at(funnelIdIdx)!;

    const offerIdIdx = isCtaPoint ? -2 : -1;
    const offerId = tags.at(offerIdIdx)!;

    const ctaIdIdx = isCtaPoint ? -1 : undefined;
    const ctaId = ctaIdIdx ? tags.at(ctaIdIdx) : undefined;

    const audience1IdIdx = 1;
    const audienceIds = tags.slice(audience1IdIdx, offerIdIdx);
    const audience1Id = audienceIds.shift()!;
    const audience2Id = audienceIds.shift();
    const audience3Id = audienceIds.shift();
    const audience4Id = audienceIds.shift();

    return {
      funnelId,
      audience1Id,
      audience2Id,
      audience3Id,
      audience4Id,
      offerId,
      ctaId,
    };
  };

  const getAudienceId = (audId: keyof ReportTags, row: RowType) => {
    const reportTags = parseFunnelAudienceContent(row);
    return reportTags[audId];
  };

  const getAudienceDisplayName = (audId: keyof ReportTags, row: RowType) => {
    const reportTags = parseFunnelAudienceContent(row);
    const audienceId = reportTags[audId];

    if (audienceId === 'control') return 'Control';
    if (audienceId === 'fallback') return 'Fallback';

    if (audienceId) {
      return rulesMap[audienceId]?.display_name ?? `Audience ${audienceId}`;
    }
  };

  const calculatedFields = ((row) => ({
    funnelId: () => {
      const reportTags = parseFunnelAudienceContent(row);
      return parseInt(reportTags.funnelId, 10);
    },
    funnelDisplayName: () => {
      const funnelAudienceContent = getFunnelAudienceContent(row);
      if (funnelAudienceContent === 'None') return 'Unknown funnel';

      const reportTags = parseFunnelAudienceContent(row);
      const funnelId = reportTags.funnelId;

      if (funnelId) {
        return funnelsMap[funnelId]?.display_name ?? `Funnel ${funnelId}`;
      }

      return 'Unknown funnel';
    },
    audienceId: () => {
      return getAudienceId('audience1Id', row);
    },
    audienceDisplayName: () => {
      const funnelAudienceContent = getFunnelAudienceContent(row);
      if (funnelAudienceContent === 'None') return 'Fallback';

      const audienceDisplayName = getAudienceDisplayName('audience1Id', row);
      return audienceDisplayName ?? 'Unknown audience';
    },
    audience1Id: () => {
      return getAudienceId('audience1Id', row);
    },
    audience1DisplayName: () => {
      const funnelAudienceContent = getFunnelAudienceContent(row);
      if (funnelAudienceContent === 'None') return 'Fallback';

      const audienceDisplayName = getAudienceDisplayName('audience1Id', row);
      return audienceDisplayName ?? 'Unknown audience';
    },
    audience2Id: () => {
      return getAudienceId('audience2Id', row);
    },
    audience2DisplayName: () => {
      return getAudienceDisplayName('audience2Id', row);
    },
    audience3Id: () => {
      return getAudienceId('audience3Id', row);
    },
    audience3DisplayName: () => {
      return getAudienceDisplayName('audience3Id', row);
    },
    audience4Id: () => {
      return getAudienceId('audience4Id', row);
    },
    audience4DisplayName: () => {
      return getAudienceDisplayName('audience4Id', row);
    },
    offerId: () => {
      const reportTags = parseFunnelAudienceContent(row);
      return reportTags.offerId;
    },
    ctaId: () => {
      const reportTags = parseFunnelAudienceContent(row);
      return reportTags.ctaId;
    },
    ctaText: () => {
      const reportTags = parseFunnelAudienceContent(row);
      const ctaId = reportTags.ctaId;
      if (ctaId) {
        return ctasTextMap[ctaId]?.text ?? `CTA ${ctaId}`;
      }

      return 'Unknown';
    },
    brandName: () => {
      if (selectedBrand) {
        return selectedBrand.label;
      }

      return 'Unknown brand';
    },
    offerName: () => {
      const reportTags = parseFunnelAudienceContent(row);
      const contentId = reportTags.offerId;
      if (contentId) {
        return offersAndLAsByIdMap[contentId]?.display_name ?? 'Unknown offer';
      }
      return 'Unknown offer';
    },
    offerCategoryId: () => {
      const reportTags = parseFunnelAudienceContent(row);
      const contentId = reportTags.offerId;
      return offersAndLAsByIdMap[contentId]?.category;
    },
    offerCategory: () => {
      const reportTags = parseFunnelAudienceContent(row);
      const contentId = reportTags.offerId;
      if (contentId) {
        const content = offersAndLAsByIdMap[contentId];
        return content
          ? renderCategoryName(content.category)
          : 'Unknown category';
      }
      return 'Unknown category';
    },
    offerPlacement: () => {
      const reportTags = parseFunnelAudienceContent(row);
      const contentId = reportTags.offerId;
      if (contentId) {
        const offer = offersAndLAsByIdMap[contentId];
        if (offer && isLossAversion(offer)) {
          return 'Loss aversion';
        }
      }
      return 'Modal';
    },
    acceptRateCalc: () => {
      const totalViews =
        row.totalViews === 0 && isDefined(row.ctaTotalViews)
          ? row.ctaTotalViews
          : row.totalViews;
      return (row.acceptedOfferCount / totalViews) * 100;
    },
    saveRateCalc: () => (row.saveCount / row.convRateViewed) * 100,
    firstTimeRepurchaseRate: () =>
      (row.deflect_payment_sessions / row.acceptedOfferCount) * 100,
    lostAccepts: () => row.acceptedOfferCount - row.nonCanceledCount,
    watchlistAccepts: () => row.nonCanceledCount - row.saveCount,
  })) satisfies PivotT.CalculatedFields<RowType>;

  type RowType = (OfferPerfV2Point | OfferPerfCtaPoint) & {
    ctaTotalViews?: number;
  };
  type PivotRow = PivotT.ComputedFields<RowType, typeof calculatedFields>;
  type PivotValues = PivotT.PivotValue<PivotRow>[];
  type GroupField = PivotT.GroupField<PivotRow>;

  const pivotRows = useMemo(() => {
    const result: GroupField[] = [
      {
        field: ['audience1DisplayName'],
        icon: NavIcons.audiences,
        name: 'audience',
        label: 'Audiences',
      },
      {
        field: ['funnelDisplayName'],
        icon: NavIcons.pages,
        name: 'experience',
        label: 'Experiences',
      },
      {
        field: ['offerPlacement'],
        icon: NavIcons.lossAversions,
        name: 'placement',
        label: 'Offer placement',
      },
      {
        field: ['offerCategory'],
        icon: NavIcons.lossAversions,
        name: 'category',
        label: 'Offer category',
      },
      {
        field: ['offerName'],
        icon: NavIcons.offers2,
        name: 'offer',
        label: 'Offer name',
        tooltip: (row) => {
          const offer = offersAndLAsByIdMap[row.offerId];
          if (!offer) return null;
          const card = offer.cards.find(
            (c) => c.name === offer.primary_card_name
          ) as RestLossAversionCardOrModalCard;
          return (
            <PreviewWrapper className="tw-zoom-40">
              <PreviewOverlay />
              <AsyncThemeWrapper modal={isModalCard(card)}>
                <CardFactory card={card} isPreview offerManagementPage />
              </AsyncThemeWrapper>
            </PreviewWrapper>
          );
        },
      },
      {
        field: ['ctaText'],
        icon: NavIcons.cta,
        name: 'cta',
        label: 'CTA Text',
      },
    ];

    if (isNestedAudiencesEnabled) {
      result[0]!.field = [
        'audience1DisplayName',
        'audience2DisplayName',
        'audience3DisplayName',
        'audience4DisplayName',
      ];
    }
    return result;
  }, [isNestedAudiencesEnabled, offersAndLAsByIdMap]);

  const genericPivotValues: PivotValues = [
    {
      label: 'Views',
      field: 'totalViews',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Accepted offer',
      field: 'acceptedOfferCount',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Offer Accept Rate',
      field: 'acceptRateCalc',
      format: NumberKit.formatPercent,
      tooltip: (row) => {
        const totalViews =
          row.totalViews === 0 && isDefined(row.ctaTotalViews)
            ? row.ctaTotalViews
            : row.totalViews;
        const totalViewsLabel =
          row.totalViews === 0 && isDefined(row.ctaTotalViews)
            ? 'CTA Views'
            : 'Offer Views';
        return (
          <PivotTableTooltipContent
            formula={`Accepted Offers / ${totalViewsLabel}`}
            calculation={
              <>
                {row.acceptedOfferCount ?? 0} / {totalViews} ={' '}
                {NumberKit.formatPercent(row.acceptRateCalc)}
              </>
            }
          />
        );
      },
    },
    {
      label: 'Saves',
      field: 'saveCount',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Lost Accepts',
      field: 'lostAccepts',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Watchlist Accepts',
      field: 'watchlistAccepts',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Offer conversion rate',
      field: 'saveRateCalc',
      format: NumberKit.formatPercent,
      tooltip: (row) => (
        <PivotTableTooltipContent
          formula="Saves / Total accepts"
          calculation={
            <>
              {row.saveCount} / {row.convRateViewed} ={' '}
              {NumberKit.formatPercent(row.saveRateCalc)}
            </>
          }
        />
      ),
    },
  ];

  const revenuePivotValues: PivotValues = [
    {
      label: 'Accepted offer (revenue)',
      field: 'acceptedOfferSavedRevenue',
      format: NumberKit.formatMoney,
    },
    {
      label: 'Saved revenue',
      field: 'saveCountSavedRevenue',
      format: NumberKit.formatMoney,
    },
  ];

  const repurchasePivotValues: PivotValues = [
    {
      label: 'First repurchase order',
      field: 'deflect_payment_sessions',
      format: NumberKit.formatNumber,
    },
    {
      label: 'First time repurchase rate',
      field: 'firstTimeRepurchaseRate',
      format: NumberKit.formatPercent,
      tooltip: (row) => (
        <PivotTableTooltipContent
          formula="First Repurchase Order / Offer accepts"
          calculation={
            <>
              {row.deflect_payment_sessions} / {row.acceptedOfferCount} ={' '}
              {NumberKit.formatPercent(row.firstTimeRepurchaseRate)}
            </>
          }
        />
      ),
    },
    {
      label: 'Cumulative retained revenue',
      field: 'deflect_payment_revenue',
      format: NumberKit.formatMoney,
    },
    {
      label: 'Total # of repurchase orders',
      field: 'deflect_payments_count',
      format: NumberKit.formatNumber,
    },
  ];

  const ctaPivotValues: PivotValues = [
    {
      label: 'CTA Views',
      field: 'ctaTotalViews',
      format: NumberKit.formatNumber,
    },
  ];

  const pivotValues = useMemo(() => {
    if (isDemoData) return genericPivotValues;
    let updatedPivotValues = genericPivotValues;
    if (mrrActive) {
      updatedPivotValues = produce(updatedPivotValues, (draft) => {
        draft.splice(2, 0, revenuePivotValues[0]!);
        draft.splice(5, 0, revenuePivotValues[1]!);
      });
    }
    if (isRepurchaseAttributionEnabled) {
      updatedPivotValues = produce(updatedPivotValues, (draft) => {
        draft.push(...repurchasePivotValues);
      });
    }
    updatedPivotValues = produce(updatedPivotValues, (draft) => {
      draft.splice(1, 0, ...ctaPivotValues);
    });
    return updatedPivotValues;
  }, [isDemoData, mrrActive, isRepurchaseAttributionEnabled]);

  const nonRevenueColumns = [
    'totalViews',
    'acceptedOfferCount',
    'acceptRateCalc',
    'saveCount',
    'saveRateCalc',
    'lostAccepts',
    'watchlistAccepts',
  ];
  const revenueColumns = [
    'totalViews',
    'acceptedOfferCount',
    'acceptedOfferSavedRevenue',
    'acceptRateCalc',
    'saveCount',
    'saveCountSavedRevenue',
    'saveRateCalc',
    'lostAccepts',
    'watchlistAccepts',
  ];

  const visibleColumns = useMemo(() => {
    if (initVisibleColumns) return initVisibleColumns;
    let result = nonRevenueColumns;
    if (mrrActive) {
      result = revenueColumns;
    }
    return result;
  }, [initVisibleColumns, mrrActive]);

  return (
    <div>
      <div className="tw-mb-3 tw-mt-2 tw-flex tw-items-center tw-gap-2">
        <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-primary-100 tw-bg-primary-50 tw-p-2">
          <SIcon name="circle-x" size={18} className="tw-text-primary-600" />
        </div>
        <span className="tw-text-lg tw-font-semibold tw-text-neutral-900">
          Offer performance report
        </span>
      </div>
      <div className="tw-text-sm tw-font-light tw-text-neutral-600">
        Select an offer to show a breakdown by experience (cancel page).
      </div>
      <div className="tw-mb-6">
        <PivotTableComponent
          tableName="ChargebeeRetention_Offer_Performance"
          isFetching={isFetching}
          isExportDisabled={isStarterFreePlan || isDemoData}
          exportTooltip={isStarterFreePlan ? exportBtnTooltip : undefined}
          data={ctaDataOffers.length > 0 ? ctaDataOffers : dataOffers}
          calculatedFields={calculatedFields}
          pivotValues={pivotValues}
          pivotRows={pivotRows}
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={(value) => {
            dispatch(
              appPersistSlice.actions.saveOfferPerfReportNavState({
                appKey,
                visibleColumns: value,
              })
            );
          }}
          groupByOpts={initGroupByOpts ?? ['offer']}
          onGroupByOptsChange={(value) => {
            dispatch(
              appPersistSlice.actions.saveOfferPerfReportNavState({
                appKey,
                groupBy: value.filter(isDefined),
              })
            );
          }}
        />
      </div>
    </div>
  );
};

export default PlayOfferPerf;
